/**
 * Spotify Web API Service
 * Implements IMusicPlatformService for Spotify integration
 */

import { 
  IMusicPlatformService, 
  MusicPlatformType, 
  MusicPlatformCapabilities,
  UserProfile,
  Track,
  Artist,
  PlatformConfig
} from '../types/MusicPlatform';
import { SpotifySearch } from './SpotifySearch';
import { SpotifyRecentlyPlayed } from './SpotifyRecentlyPlayed';

interface SpotifyTokenData {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  expires_at: number;
  scope: string;
}

interface SpotifyUser {
  id: string;
  display_name: string;
  email: string;
  images: Array<{ url: string; height: number; width: number; }>;
  product: 'free' | 'premium';
  country: string;
  followers: { total: number };
}

interface SpotifyArtist {
  id: string;
  name: string;
  external_urls: { spotify: string };
  images: Array<{ url: string; height: number; width: number; }>;
  followers: { total: number };
  genres: string[];
  popularity: number;
}

interface SpotifyTrack {
  id: string;
  name: string;
  artists: SpotifyArtist[];
  album: {
    id: string;
    name: string;
    images: Array<{ url: string; height: number; width: number; }>;
  };
  duration_ms: number;
  external_urls: { spotify: string };
  preview_url: string | null;
}

export class SpotifyService implements IMusicPlatformService {
  readonly platformType: MusicPlatformType = 'spotify';
  readonly capabilities: MusicPlatformCapabilities = {
    canCreatePlaylists: true,
    canAddToLibrary: true,
    canAccessLibrary: true,
    canGetRecentlyPlayed: true,
    requiresPremium: ['full-track-playback'], // Only full playback requires premium
    maxPlaylistSize: 10000
  };

  private static instance: SpotifyService;
  private clientId: string;
  private redirectUri: string;
  private scopes: string[];
  private readonly baseUrl = 'https://api.spotify.com/v1';
  private spotifySearch: SpotifySearch;
  private spotifyRecentlyPlayed: SpotifyRecentlyPlayed;
  
  // OAuth tokens
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiresAt: number | null = null;
  
  // Rate limiting
  private lastRequestTime = 0;
  private minInterval = 100; // 100ms minimum between requests
  private rateLimitRemaining = 1000; // Conservative default

  constructor(config?: PlatformConfig) {
    this.clientId = config?.clientId || process.env.REACT_APP_SPOTIFY_CLIENT_ID || '';
    this.redirectUri = config?.redirectUri || `${window.location.origin}/spotify/callback`;
    this.scopes = config?.scopes || [
      'user-read-recently-played',
      'user-library-read',
      'playlist-modify-public',
      'playlist-modify-private',
      'user-read-private'
    ];

    if (!this.clientId) {
      throw new Error('Spotify client ID is required');
    }

    // Initialize service dependencies
    this.spotifySearch = SpotifySearch.getInstance();
    this.spotifyRecentlyPlayed = SpotifyRecentlyPlayed.getInstance();
  }

  static getInstance(config?: PlatformConfig): SpotifyService {
    if (!SpotifyService.instance) {
      SpotifyService.instance = new SpotifyService(config);
    }
    return SpotifyService.instance;
  }

  /**
   * Authorize user with Spotify using PKCE flow
   */
  async authorize(): Promise<void> {
    console.log('🟢 Starting Spotify authorization...');
    console.log('🟢 Redirect URI:', this.redirectUri);
    console.log('🟢 Client ID:', this.clientId ? 'Set' : 'Missing');
    
    try {
      // Generate PKCE challenge
      const codeVerifier = this.generateCodeVerifier();
      const codeChallenge = await this.generateCodeChallenge(codeVerifier);
      
      // Store code verifier for later use (in both storages for reliability)
      localStorage.setItem('spotify_code_verifier', codeVerifier);
      sessionStorage.setItem('spotify_code_verifier', codeVerifier);
      console.log('🟢 Stored code verifier for PKCE flow');
      
      // Build authorization URL
      const authUrl = new URL('https://accounts.spotify.com/authorize');
      authUrl.searchParams.append('client_id', this.clientId);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('redirect_uri', this.redirectUri);
      authUrl.searchParams.append('scope', this.scopes.join(' '));
      authUrl.searchParams.append('code_challenge_method', 'S256');
      authUrl.searchParams.append('code_challenge', codeChallenge);
      authUrl.searchParams.append('state', this.generateState());
      
      console.log('🟢 Authorization scopes:', this.scopes);
      console.log('🟢 Redirecting to Spotify authorization...');
      window.location.href = authUrl.toString();
      
    } catch (error) {
      console.error('🟢 ❌ Spotify authorization failed:', error);
      throw new Error('Failed to start Spotify authorization');
    }
  }

  /**
   * Handle authorization callback from Spotify
   */
  async handleAuthorizationCallback(code: string, state?: string): Promise<void> {
    console.log('🟢 Handling Spotify authorization callback...');
    
    try {
      // Try both storage methods
      let codeVerifier = localStorage.getItem('spotify_code_verifier');
      if (!codeVerifier) {
        codeVerifier = sessionStorage.getItem('spotify_code_verifier');
        console.log('🟢 Code verifier from sessionStorage:', codeVerifier ? 'Found' : 'Missing');
      } else {
        console.log('🟢 Code verifier from localStorage:', codeVerifier ? 'Found' : 'Missing');
      }
      
      // Debug: Check all storage keys
      console.log('🟢 localStorage keys:', Object.keys(localStorage));
      console.log('🟢 sessionStorage keys:', Object.keys(sessionStorage));
      
      if (!codeVerifier) {
        throw new Error('Missing code verifier - unable to complete OAuth flow');
      }
      
      // Exchange code for tokens
      const tokenResponse = await this.exchangeCodeForTokens(code, codeVerifier);
      
      // Store tokens
      this.accessToken = tokenResponse.access_token;
      this.refreshToken = tokenResponse.refresh_token;
      this.tokenExpiresAt = Date.now() + (tokenResponse.expires_in * 1000);
      
      // Save to localStorage
      const tokens = {
        access_token: this.accessToken,
        refresh_token: this.refreshToken,
        expires_at: this.tokenExpiresAt,
        token_type: tokenResponse.token_type,
        scope: tokenResponse.scope
      };
      
      localStorage.setItem('spotify_tokens', JSON.stringify(tokens));
      localStorage.removeItem('spotify_code_verifier');
      
      console.log('🟢 ✅ Spotify authorization completed successfully');
      console.log('🟢 Token scope:', tokenResponse.scope);
      
    } catch (error) {
      console.error('🟢 ❌ Authorization callback failed:', error);
      throw new Error('Failed to complete Spotify authorization');
    }
  }

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  async handleCallback(code: string, state: string): Promise<void> {
    console.log('🟢 Handling Spotify OAuth callback...');
    
    try {
      const codeVerifier = localStorage.getItem('spotify_code_verifier');
      if (!codeVerifier) {
        throw new Error('Code verifier not found');
      }
      
      // Exchange code for tokens
      const tokenResponse = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          code: code,
          redirect_uri: this.redirectUri,
          client_id: this.clientId,
          code_verifier: codeVerifier
        })
      });

      if (!tokenResponse.ok) {
        throw new Error(`Token exchange failed: ${tokenResponse.statusText}`);
      }

      const tokenData = await tokenResponse.json();
      
      // Store tokens
      const spotifyTokenData: SpotifyTokenData = {
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token,
        token_type: tokenData.token_type || 'Bearer',
        expires_in: tokenData.expires_in,
        expires_at: Date.now() + (tokenData.expires_in * 1000),
        scope: tokenData.scope
      };
      
      localStorage.setItem('spotify_tokens', JSON.stringify(spotifyTokenData));
      localStorage.removeItem('spotify_code_verifier');
      
      console.log('🟢 ✅ Spotify tokens stored successfully');
      
    } catch (error) {
      console.error('🟢 ❌ Token exchange failed:', error);
      throw error;
    }
  }

  /**
   * Check if user is authorized
   */
  isAuthorized(): boolean {
    return !!this.getValidToken();
  }

  /**
   * Get current access token
   */
  getToken(): string | null {
    return this.getValidToken();
  }

  /**
   * Logout and clear tokens
   */
  async logout(): Promise<void> {
    localStorage.removeItem('spotify_tokens');
    console.log('🟢 Logged out from Spotify');
  }

  /**
   * Search for artists on Spotify
   */
  async searchArtists(query: string): Promise<Artist[]> {
    return await this.spotifySearch.search(query);
  }

  /**
   * Get recently played artists
   */
  async getRecentlyPlayed(limit: number = 50): Promise<Artist[]> {
    // Use fast method for immediate response with basic artist info
    const artists = await this.spotifyRecentlyPlayed.getRecentlyPlayedArtistsFast();
    return artists.slice(0, limit);
  }

  /**
   * Get user's library artists
   */
  async getLibraryArtists(): Promise<Artist[]> {
    console.log('🟢 Getting library artists from Spotify');
    
    try {
      // Get saved albums and extract artists
      const albums = await this.apiRequest('/me/albums?limit=50');
      const artistMap = new Map<string, SpotifyArtist>();
      
      albums.items.forEach((item: { album: { artists: SpotifyArtist[] } }) => {
        item.album.artists.forEach((artist: SpotifyArtist) => {
          if (!artistMap.has(artist.id)) {
            artistMap.set(artist.id, artist);
          }
        });
      });
      
      return Array.from(artistMap.values()).map(artist => this.transformSpotifyArtist(artist));
    } catch (error) {
      console.error('🟢 Library artists fetch failed:', error);
      return [];
    }
  }

  /**
   * Get user profile
   */
  async getUserProfile(): Promise<UserProfile> {
    console.log('🟢 Getting Spotify user profile');
    
    try {
      const user: SpotifyUser = await this.apiRequest('/me');
      
      return {
        id: user.id,
        name: user.display_name || user.id,
        email: user.email,
        imageUrl: user.images?.[0]?.url,
        platform: 'spotify',
        subscriptionType: user.product
      };
    } catch (error) {
      console.error('🟢 User profile fetch failed:', error);
      throw error;
    }
  }

  /**
   * Create a new playlist
   */
  async createPlaylist(name: string, tracks: Track[]): Promise<string> {
    console.log(`🟢 Creating Spotify playlist: "${name}" with ${tracks.length} tracks`);
    
    try {
      const user = await this.getUserProfile();
      
      // Create playlist
      const playlist = await this.apiRequest(`/users/${user.id}/playlists`, {
        method: 'POST',
        body: JSON.stringify({
          name: name,
          description: `Generated playlist with ${tracks.length} tracks`,
          public: false
        })
      });
      
      // Add tracks if provided
      if (tracks.length > 0) {
        await this.addTracksToPlaylist(playlist.id, tracks);
      }
      
      return playlist.id;
    } catch (error) {
      console.error('🟢 Playlist creation failed:', error);
      throw error;
    }
  }

  /**
   * Add tracks to user's library
   */
  async addTracksToLibrary(tracks: Track[]): Promise<void> {
    console.log(`🟢 Adding ${tracks.length} tracks to Spotify library`);
    
    try {
      const trackIds = tracks
        .filter(track => track.platform === 'spotify')
        .map(track => track.id);
      
      if (trackIds.length === 0) return;
      
      // Spotify API allows max 50 tracks per request
      for (let i = 0; i < trackIds.length; i += 50) {
        const batch = trackIds.slice(i, i + 50);
        await this.apiRequest('/me/tracks', {
          method: 'PUT',
          body: JSON.stringify({ ids: batch })
        });
      }
    } catch (error) {
      console.error('🟢 Adding tracks to library failed:', error);
      throw error;
    }
  }

  /**
   * Add tracks to an existing playlist
   */
  async addTracksToPlaylist(playlistId: string, tracks: Track[]): Promise<void> {
    console.log(`🟢 Adding ${tracks.length} tracks to Spotify playlist ${playlistId}`);
    
    try {
      const trackUris = tracks
        .filter(track => track.platform === 'spotify')
        .map(track => `spotify:track:${track.id}`);
      
      if (trackUris.length === 0) return;
      
      // Spotify API allows max 100 tracks per request
      for (let i = 0; i < trackUris.length; i += 100) {
        const batch = trackUris.slice(i, i + 100);
        await this.apiRequest(`/playlists/${playlistId}/tracks`, {
          method: 'POST',
          body: JSON.stringify({ uris: batch })
        });
      }
    } catch (error) {
      console.error('🟢 Adding tracks to playlist failed:', error);
      throw error;
    }
  }

  /**
   * Get platform connection status
   */
  async getConnectionStatus(): Promise<{
    connected: boolean;
    authenticated: boolean;
    hasValidToken: boolean;
    capabilities: MusicPlatformCapabilities;
  }> {
    const hasValidToken = !!this.getValidToken();
    
    return {
      connected: !!this.clientId,
      authenticated: hasValidToken,
      hasValidToken,
      capabilities: this.capabilities
    };
  }

  // Private helper methods

  /**
   * Get valid access token (refresh if needed)
   */
  private getValidToken(): string | null {
    try {
      const tokenData = localStorage.getItem('spotify_tokens');
      if (!tokenData) return null;
      
      const tokens: SpotifyTokenData = JSON.parse(tokenData);
      
      // Check if token is expired
      if (tokens.expires_at <= Date.now()) {
        console.log('🟢 Access token expired, attempting refresh...');
        // TODO: Implement token refresh
        return null;
      }
      
      return tokens.access_token;
    } catch (error) {
      console.error('🟢 Error getting token:', error);
      return null;
    }
  }

  /**
   * Wait for rate limit before making request
   */
  private async waitForRateLimit(): Promise<void> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    // Implement progressive backoff if rate limited
    let delay = this.minInterval;
    if (this.rateLimitRemaining < 10) {
      delay = 1000; // 1 second delay when close to limit
    } else if (this.rateLimitRemaining < 50) {
      delay = 500; // 500ms delay when getting low
    }
    
    if (timeSinceLastRequest < delay) {
      await new Promise(resolve => 
        setTimeout(resolve, delay - timeSinceLastRequest)
      );
    }
    
    this.lastRequestTime = Date.now();
  }

  /**
   * Make authenticated API request to Spotify with rate limiting and retry logic
   */
  private async apiRequest(endpoint: string, options: RequestInit = {}, attempt = 1): Promise<any> {
    const token = this.getValidToken();
    if (!token) {
      throw new Error('No valid Spotify token available');
    }
    
    // Wait for rate limit
    await this.waitForRateLimit();
    
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    // Update rate limit tracking from headers
    const remaining = response.headers.get('X-RateLimit-Remaining');
    if (remaining) {
      this.rateLimitRemaining = parseInt(remaining);
    }
    
    // Handle rate limiting (429) with exponential backoff
    if (response.status === 429) {
      if (attempt >= 3) {
        throw new Error(`Spotify API rate limited after ${attempt} attempts`);
      }
      
      const retryAfter = response.headers.get('Retry-After');
      const delay = retryAfter ? parseInt(retryAfter) * 1000 : Math.pow(2, attempt) * 1000;
      
      console.log(`🟢 Rate limited, waiting ${delay}ms before retry ${attempt + 1}/3`);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return this.apiRequest(endpoint, options, attempt + 1);
    }
    
    if (!response.ok) {
      throw new Error(`Spotify API error: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  /**
   * Transform Spotify artist to unified Artist interface
   */
  private transformSpotifyArtist(spotifyArtist: SpotifyArtist): Artist {
    return {
      name: spotifyArtist.name,
      sortName: spotifyArtist.name,
      verified: true,
      source: 'spotify',
      platform: 'spotify',
      externalUrl: spotifyArtist.external_urls.spotify,
      followers: spotifyArtist.followers?.total,
      genres: spotifyArtist.genres,
      artwork: spotifyArtist.images?.[0] ? {
        url: spotifyArtist.images[0].url,
        width: spotifyArtist.images[0].width,
        height: spotifyArtist.images[0].height
      } : undefined
    };
  }

  /**
   * Generate PKCE code verifier
   */
  private generateCodeVerifier(): string {
    // Generate 96 random bytes for a 128 character verifier
    const array = new Uint8Array(96);
    crypto.getRandomValues(array);
    // Convert to base64url
    const base64 = btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
    
    // Ensure it's between 43-128 characters (take first 128 if longer)
    const verifier = base64.substring(0, 128);
    console.log('🟢 Generated code verifier length:', verifier.length);
    return verifier;
  }

  /**
   * Generate PKCE code challenge
   */
  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }

  /**
   * Exchange authorization code for access tokens
   */
  private async exchangeCodeForTokens(code: string, codeVerifier: string): Promise<any> {
    const tokenUrl = 'https://accounts.spotify.com/api/token';
    
    console.log('🟢 Token exchange params:', {
      redirect_uri: this.redirectUri,
      client_id: this.clientId,
      code_length: code.length,
      verifier_length: codeVerifier.length
    });
    
    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('code', code);
    params.append('redirect_uri', this.redirectUri);
    params.append('client_id', this.clientId);
    params.append('code_verifier', codeVerifier);
    
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString()
    });
    
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token exchange failed: ${response.status} - ${error}`);
    }
    
    return await response.json();
  }

  /**
   * Generate random state for OAuth
   */
  private generateState(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }
}

export default SpotifyService;